"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type CSSProperties,
  type ReactNode,
} from "react";
import { DownloadAppButton } from "@/components/pillar/DownloadAppButton";
import type { PillarOverlayEntry } from "@/components/pillar/pillar-overlay-content";

interface PillarOverlayContextValue {
  openOv: (id: string) => void;
  closeOv: () => void;
}

const PillarOverlayContext = createContext<PillarOverlayContextValue | null>(null);

/**
 * Owns the "Read in Details" overlay state for the whole pillar page.
 * Overlay copy is rendered on the server and passed in via `overlays`.
 */
export function PillarOverlayProvider({
  overlays,
  children,
}: {
  overlays: Record<string, PillarOverlayEntry>;
  children: ReactNode;
}) {
  const [activeOverlay, setActiveOverlay] = useState<string | null>(null);

  const openOv = useCallback((id: string) => {
    setActiveOverlay(id);
    document.body.style.overflow = "hidden";
  }, []);

  const closeOv = useCallback(() => {
    setActiveOverlay(null);
    document.body.style.overflow = "";
  }, []);

  // Close overlay on ESC
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeOv();
    };
    document.addEventListener("keydown", handleEsc);
    return () => document.removeEventListener("keydown", handleEsc);
  }, [closeOv]);

  const value = useMemo(() => ({ openOv, closeOv }), [openOv, closeOv]);

  return (
    <PillarOverlayContext.Provider value={value}>
      {children}
      {activeOverlay && (
        <div
          className={`overlay ${activeOverlay ? "active" : ""}`}
          onClick={(e) => e.target === e.currentTarget && closeOv()}
        >
          <OverlayContent entry={overlays[activeOverlay]} closeOv={closeOv} />
        </div>
      )}
    </PillarOverlayContext.Provider>
  );
}

function OverlayContent({
  entry,
  closeOv,
}: {
  entry: PillarOverlayEntry | undefined;
  closeOv: () => void;
}) {
  if (!entry) return null;

  return (
    <div className="ov-panel">
      <div className="ov-header">
        <div className="ov-header-left">
          <h2>{entry.title}</h2>
          <p>{entry.subtitle}</p>
        </div>
        <button className="ov-close" onClick={closeOv}>
          ✕
        </button>
      </div>
      <div className="ov-body">
        {entry.body}
        <div className="ov-cta">
          <p>Download RealtorsIn — Free for All UAE Real Estate Professionals</p>
          <DownloadAppButton className="ov-store-link btn-gold" />
          <p className="ov-web-note">🌐 Web version launching soon — download the app today.</p>
        </div>
      </div>
    </div>
  );
}

/** Button that opens a feature overlay. Label/icon are supplied by the server. */
export function PillarDetailsButton({
  ovId,
  className = "details-btn",
  style,
  children,
}: {
  ovId: string;
  className?: string;
  style?: CSSProperties;
  children: ReactNode;
}) {
  const ctx = useContext(PillarOverlayContext);

  return (
    <button
      className={className}
      type="button"
      style={style}
      onClick={() => ctx?.openOv(ovId)}
    >
      {children}
    </button>
  );
}
